import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { SpecraText } from '../ensigns/typography';
import { SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
const RAMP = [
    'bg-[var(--chart-heat-0)]',
    'bg-[var(--chart-heat-1)]',
    'bg-[var(--chart-heat-2)]',
    'bg-[var(--chart-heat-3)]',
    'bg-[var(--chart-heat-4)]'
];
// Literal classes so Tailwind emits them: a working week, or a full one when
// something falls due on a weekend.
const GRID = {
    5: 'grid-cols-[var(--space-12)_repeat(5,minmax(0,1fr))]',
    7: 'grid-cols-[var(--space-12)_repeat(7,minmax(0,1fr))]'
};
const FLAG_RING = {
    warn: 'ring-2 ring-inset ring-[var(--chart-warn)]',
    bad: 'ring-2 ring-inset ring-[var(--chart-bad)]'
};
/** The ramp step of a count: 0 for none, then four steps up to the busiest day. */ export function heatLevel(count, max) {
    if (count <= 0 || max <= 0) return 0;
    return Math.min(4, Math.max(1, Math.ceil(count / max * 4)));
}
/** Due dates per working day as a heat grid with at-risk and late outlines. */ export function SpecraCalendarHeat({ weeks, weekdays = [
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri'
], unit = 'due', emptyMessage, renderLink, className, getDayTestId, title, description, actions, insight, 'data-testid': testId }) {
    const days = weeks.flatMap((w)=>w.days);
    const max = Math.max(0, ...days.map((d)=>d.count));
    const empty = max === 0;
    const table = {
        columns: [
            {
                key: 'day',
                label: 'Day'
            },
            {
                key: 'count',
                label: `Count ${unit}`,
                numeric: true
            },
            {
                key: 'flag',
                label: 'Includes'
            }
        ],
        rows: days.map((d)=>({
                id: d.date,
                cells: {
                    day: d.label,
                    count: d.count.toLocaleString(),
                    flag: d.flag === 'bad' ? 'Late' : d.flag === 'warn' ? 'At risk' : ''
                }
            }))
    };
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: [
                {
                    label: 'Includes at risk',
                    tone: 'warn',
                    shape: 'outline'
                },
                {
                    label: 'Includes late',
                    tone: 'bad',
                    shape: 'outline'
                }
            ],
            children: /*#__PURE__*/ _jsxs("li", {
                className: "flex items-center gap-[var(--space-1)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Fewer"
                    }),
                    RAMP.map((c)=>/*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            className: cn('size-2.5 rounded-[var(--radius-xs)]', c)
                        }, c)),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "More"
                    })
                ]
            })
        }),
        table: table,
        title: title,
        children: /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
            delayDuration: 100,
            children: /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        "aria-hidden": "true",
                        className: cn('grid gap-[var(--space-1)]', GRID[weekdays.length] ?? GRID[5]),
                        children: [
                            /*#__PURE__*/ _jsx("span", {}),
                            weekdays.map((d)=>/*#__PURE__*/ _jsx(SpecraText, {
                                    className: "text-center",
                                    size: "micro",
                                    tone: "muted",
                                    children: d
                                }, d))
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-[var(--space-1)] space-y-[var(--space-1)]",
                        children: weeks.map((w)=>/*#__PURE__*/ _jsxs("div", {
                                className: cn('grid items-center gap-[var(--space-1)]', GRID[weekdays.length] ?? GRID[5]),
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        className: "whitespace-nowrap",
                                        size: "micro",
                                        tone: "muted",
                                        children: w.label
                                    }),
                                    w.days.map((d)=>{
                                        const level = empty ? 0 : heatLevel(d.count, max);
                                        const cell = /*#__PURE__*/ _jsx(SpecraChartLink, {
                                            "aria-label": `${d.label}: ${d.count.toLocaleString()} ${unit}${d.flag === 'bad' ? ', includes late' : d.flag === 'warn' ? ', includes at risk' : ''}`,
                                            className: cn('flex h-[var(--component-chart-heat-cell-height)] items-center justify-center rounded-[var(--radius-sm)] transition-[filter] hover:brightness-95', RAMP[level], d.flag && !empty && FLAG_RING[d.flag], d.today && !empty && 'outline outline-1 outline-offset-1 outline-[color:var(--text-primary)]', d.past && 'opacity-50'),
                                            "data-flag": d.flag,
                                            "data-item-id": d.date,
                                            "data-testid": getDayTestId?.(d),
                                            href: empty ? undefined : d.href,
                                            renderLink: renderLink,
                                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                                "aria-hidden": "true",
                                                className: cn('tabular-nums', level >= 3 && 'text-[color:var(--chart-heat-ink-strong)]'),
                                                size: "micro",
                                                tone: level >= 3 ? undefined : 'muted',
                                                children: d.count > 0 ? d.count.toLocaleString() : ''
                                            })
                                        }, d.date);
                                        return empty ? /*#__PURE__*/ _jsx("span", {
                                            children: cell
                                        }, d.date) : /*#__PURE__*/ _jsx(SpecraTooltip, {
                                            content: /*#__PURE__*/ _jsxs("span", {
                                                className: "block",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "block font-semibold",
                                                        children: [
                                                            d.count.toLocaleString(),
                                                            " ",
                                                            unit
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "block",
                                                        children: d.label
                                                    }),
                                                    d.detail ? /*#__PURE__*/ _jsx("span", {
                                                        className: "block",
                                                        children: d.detail
                                                    }) : null
                                                ]
                                            }),
                                            delayDuration: 100,
                                            children: cell
                                        }, d.date);
                                    })
                                ]
                            }, w.id))
                    }),
                    empty ? /*#__PURE__*/ _jsx("div", {
                        className: "absolute inset-0 flex items-center justify-center",
                        children: /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                            "data-chart-empty": "",
                            size: "body-ui",
                            tone: "muted",
                            children: emptyMessage
                        })
                    }) : null
                ]
            })
        })
    });
}
